import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { openProductDetailModal } from '../../../../../store/products/products.actions';
import { messages } from './supplier-products-columns.messages';
import type { SupplierProduct } from '../../types/supplier-product-types';
import type { Row } from 'react-table';

type ProductSkuCellProps = {
  data: SupplierProduct;
};

const ProductSkuCell = ({ data }: ProductSkuCellProps) => {
  const dispatch = useDispatch();
  const onClick = useCallback(() => {
    dispatch(openProductDetailModal(data.productId));
  }, [dispatch, data.productId]);

  return (
    <Button onClick={onClick} theme={THEME_CONSTANTS.link}>
      {data.productSku}
    </Button>
  );
};

type ActionsCellProps = {
  data: SupplierProduct;
  onEdit: (product: SupplierProduct) => void;
  onDelete: (product: SupplierProduct) => void;
};

const ActionsCell = ({ data, onEdit, onDelete }: ActionsCellProps) => {
  const intl = useIntl();

  return (
    <FlexRow gap={Gap.Space8}>
      <Button ariaLabel={intl.formatMessage(messages.editButtonLabel)} onClick={() => onEdit(data)} theme={THEME_CONSTANTS.unstyled}>
        <FontAwesomeIcon icon={faPencil} />
      </Button>
      <Button ariaLabel={intl.formatMessage(messages.deleteButtonLabel)} onClick={() => onDelete(data)} theme={THEME_CONSTANTS.unstyled}>
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </FlexRow>
  );
};

type GetSupplierProductColumnsParams = {
  onEdit: (product: SupplierProduct) => void;
  onDelete: (product: SupplierProduct) => void;
};

export const getSupplierProductColumns = ({ onEdit, onDelete }: GetSupplierProductColumnsParams) => [
  {
    id: 'selection',
    Header: ({
      getToggleAllRowsSelectedProps,
    }: {
      getToggleAllRowsSelectedProps: () => {
        onChange: (event: { target: { checked: boolean } }) => void;
        checked: boolean;
        indeterminate: boolean;
      };
    }) => {
      const { onChange, checked, indeterminate, ...rest } = getToggleAllRowsSelectedProps();
      return (
        <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => onChange({ target: { checked: !checked } })} {...rest} />
      );
    },
    Cell: ({
      row,
    }: {
      row: Row<SupplierProduct> & {
        getToggleRowSelectedProps: () => {
          onChange: (event: { target: { checked: boolean } }) => void;
          checked: boolean;
          indeterminate: boolean;
        };
      };
    }) => {
      const { onChange, checked, indeterminate, ...rest } = row.getToggleRowSelectedProps();
      return (
        <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => onChange({ target: { checked: !checked } })} {...rest} />
      );
    },
    width: 50,
    minWidth: 50,
    maxWidth: 50,
  },
  {
    Header: <FormattedMessage {...messages.productNameColumnHeader} />,
    accessor: 'productName' as const,
    width: 250,
  },
  {
    Header: <FormattedMessage {...messages.productSkuColumnHeader} />,
    accessor: 'productSku' as const,
    Cell: ({ row }: { row: { original: SupplierProduct } }) => <ProductSkuCell data={row.original} />,
    width: 150,
  },
  {
    Header: <FormattedMessage {...messages.supplierSkuColumnHeader} />,
    accessor: 'supplierSku' as const,
    width: 150,
  },
  {
    Header: <FormattedMessage {...messages.unitCostColumnHeader} />,
    accessor: 'cost' as const,
    Cell: ({ value }: { value: number }) => <FormattedNumber currency='USD' style='currency' value={value ?? 0} />,
    width: 120,
  },
  {
    Header: <FormattedMessage {...messages.actionsColumnHeader} />,
    accessor: 'productSupplierGuid' as const,
    Cell: ({ row }: { row: { original: SupplierProduct } }) => <ActionsCell data={row.original} onDelete={onDelete} onEdit={onEdit} />,
    width: 100,
    disableSortBy: true,
  },
];
